import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  AlertCircle,
  CheckCircle2,
  Download,
  Edit,
  Eye,
  EyeOff,
  FileSpreadsheet,
  GripVertical,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  formatUploadSize,
  getUploadSizeError,
  MAX_UPLOAD_SIZE_MB,
  uploadFile,
  type UploadProgress,
} from '@/components/business-ui/api/files/service';
import {
  commitSpeechTopicImport,
  createSpeechTopic,
  deleteSpeechTopic,
  downloadSpeechTopicImportTemplate,
  getAdminSpeechTopics,
  previewSpeechTopicImport,
  reorderSpeechTopics,
  updateSpeechTopic,
} from '@/api/speechTopic';
import { isRequiredResourceContent, isValidPreviewAssetUrl, isValidTopicDuration } from '@shared/topic-validation';
import type {
  AdminSpeechTopic,
  AdminSpeechTopicImportPreviewResp,
  ContentCategory,
  ContentDirection,
  SuitableGrade,
} from '@shared/api.interface';
import {
  CONTENT_CATEGORY_LABELS,
  CONTENT_DIRECTION_LABELS,
  SUITABLE_GRADE_LABELS,
} from '@shared/api.interface';
import {
  filterAdminTopics,
  getMissingPreviewLabels,
  isMissingResource,
  sortAdminTopicsByMetric,
  summarizeAdminTopics,
  type TopicAdminSummary,
  type TopicIssueFilter,
  type TopicStatsSortMode,
} from './topic-admin-stats';

type TopicFormState = {
  title: string;
  cover_image: string;
  suitable_grade: SuitableGrade | '';
  content_category: ContentCategory | '';
  content_direction: ContentDirection | '';
  estimated_duration: string;
  content_description: string;
  script_display: string;
  core_value: string;
  video_display: string;
  ppt_display: string;
  download_link: string;
  is_active: boolean;
};

const emptyTopicForm: TopicFormState = {
  title: '',
  cover_image: '',
  suitable_grade: '',
  content_category: '',
  content_direction: '',
  estimated_duration: '',
  content_description: '',
  script_display: '',
  core_value: '',
  video_display: '',
  ppt_display: '',
  download_link: '',
  is_active: true,
};

const categoryOptions = Object.entries(CONTENT_CATEGORY_LABELS) as Array<[ContentCategory, string]>;
const directionOptions = Object.entries(CONTENT_DIRECTION_LABELS) as Array<[ContentDirection, string]>;
const gradeOptions = Object.entries(SUITABLE_GRADE_LABELS) as Array<[SuitableGrade, string]>;

function extractApiErrorMessage(error: any, fallback: string): string {
  const data = error?.response?.data;
  if (typeof data?.message === 'string' && data.message.trim()) return data.message.trim();
  if (Array.isArray(data?.message) && typeof data.message[0] === 'string') return data.message[0];
  if (typeof data?.error?.message === 'string' && data.error.message.trim()) return data.error.message.trim();
  return fallback;
}

function formFromTopic(topic: AdminSpeechTopic): TopicFormState {
  return {
    title: topic.title,
    cover_image: topic.cover_image || '',
    suitable_grade: topic.suitable_grade,
    content_category: topic.content_category,
    content_direction: topic.content_direction,
    estimated_duration: topic.estimated_duration || '',
    content_description: topic.content_description || '',
    script_display: topic.script_display || '',
    core_value: topic.core_value || '',
    video_display: topic.video_display || '',
    ppt_display: topic.ppt_display || '',
    download_link: topic.download_link || '',
    is_active: topic.is_active,
  };
}

function trimPayload(form: TopicFormState) {
  return {
    title: form.title.trim(),
    cover_image: form.cover_image.trim() || null,
    suitable_grade: form.suitable_grade as SuitableGrade,
    content_category: form.content_category as ContentCategory,
    content_direction: form.content_direction as ContentDirection,
    estimated_duration: form.estimated_duration.trim(),
    content_description: form.content_description.trim(),
    script_display: form.script_display.trim() || null,
    core_value: form.core_value.trim(),
    video_display: form.video_display.trim() || null,
    ppt_display: form.ppt_display.trim() || null,
    download_link: form.download_link.trim(),
    is_active: form.is_active,
  };
}

export function TopicAdminPanel() {
  const [topics, setTopics] = useState<AdminSpeechTopic[]>([]);
  const [search, setSearch] = useState('');
  // 上架/下架两个清晰页签，默认上架
  const [status, setStatus] = useState<'active' | 'inactive'>('active');
  const [sortMode, setSortMode] = useState<TopicStatsSortMode>('display');
  const [categoryFilter, setCategoryFilter] = useState<ContentCategory | ''>('');
  const [issueFilter, setIssueFilter] = useState<TopicIssueFilter>('all');
  const [summary, setSummary] = useState<TopicAdminSummary | null>(null);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof TopicFormState, string>>>({});
  const [editingTopic, setEditingTopic] = useState<AdminSpeechTopic | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [form, setForm] = useState<TopicFormState>(emptyTopicForm);
  const [uploadingField, setUploadingField] = useState<keyof TopicFormState | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [importPreview, setImportPreview] = useState<AdminSpeechTopicImportPreviewResp | null>(null);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [uploadSuccessField, setUploadSuccessField] = useState<keyof Pick<TopicFormState, 'cover_image' | 'video_display' | 'ppt_display' | 'script_display'> | null>(null);
  const importFileRef = useRef<HTMLInputElement | null>(null);
  const saveLockRef = useRef(false);
  const uploadLockRef = useRef(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const PAGE_SIZE = 20;

  const validImportItems = useMemo(() => {
    return importPreview?.rows.flatMap((row) => row.item ? [row.item] : []) || [];
  }, [importPreview]);

  const fetchTopics = useCallback(async () => {
    setIsLoading(true);
    try {
      // 统计卡需要上架 + 下架全部主题，与当前列表并行获取
      const [result, all] = await Promise.all([
        getAdminSpeechTopics({ search, status }),
        getAdminSpeechTopics({ status: 'all' }).catch(() => null),
      ]);
      setTopics(result.items);
      setSummary(all ? summarizeAdminTopics(all.items) : null);
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '获取主题列表失败'));
    } finally {
      setIsLoading(false);
    }
  }, [search, status]);

  // 切换页签/搜索时回到第一页
  useEffect(() => {
    setPage(1);
  }, [search, status, sortMode, categoryFilter, issueFilter]);

  // 删除/上下架导致当前页变空时回退页码
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(topics.length / PAGE_SIZE));
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [topics.length, page]);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  const openCreateDialog = () => {
    setEditingTopic(null);
    setForm(emptyTopicForm);
    setFormError('');
    setFieldErrors({});
    setUploadSuccessField(null);
    setIsFormOpen(true);
  };

  const openEditDialog = (topic: AdminSpeechTopic) => {
    setEditingTopic(topic);
    setForm(formFromTopic(topic));
    setFormError('');
    setFieldErrors({});
    setUploadSuccessField(null);
    setIsFormOpen(true);
  };

  const updateForm = <K extends keyof TopicFormState>(key: K, value: TopicFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFormError('');
    if (key === 'cover_image' || key === 'video_display' || key === 'ppt_display' || key === 'script_display') {
      setUploadSuccessField((current) => current === key ? null : current);
    }
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleUpload = async (field: keyof Pick<TopicFormState, 'cover_image' | 'video_display' | 'ppt_display' | 'script_display'>, file?: File) => {
    if (!file || uploadingField || uploadLockRef.current) return;
    const sizeError = getUploadSizeError(file);
    if (sizeError) {
      setFieldErrors((prev) => ({ ...prev, [field]: sizeError }));
      setUploadProgress(null);
      return;
    }
    if ((field === 'ppt_display' || field === 'script_display') && !file.type.startsWith('image/')) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: `${field === 'script_display' ? '文稿预览图' : 'PPT预览图'}只支持图片文件`,
      }));
      return;
    }
    uploadLockRef.current = true;
    setUploadingField(field);
    setUploadProgress(null);
    try {
      const result = await uploadFile(
        file,
        field === 'script_display' ? 'script_preview' : undefined,
        (progress) => setUploadProgress(progress),
      );
      updateForm(field, result.url);
      setUploadSuccessField(field);
    } catch (error) {
      const message = extractApiErrorMessage(error, '上传失败');
      setFieldErrors((prev) => ({
        ...prev,
        [field]: message,
      }));
      // 失败可见：避免“一直转圈”，明确提示并可重新上传
      const fieldName: Record<string, string> = {
        cover_image: '封面',
        video_display: '视频预览',
        ppt_display: 'PPT预览图',
        script_display: '文稿预览图',
      };
      toast.error(`${fieldName[field] ?? '文件'}上传失败：${message}`);
    } finally {
      uploadLockRef.current = false;
      setUploadingField(null);
      setUploadProgress(null);
    }
  };

  const handleSave = async () => {
    if (saveLockRef.current || isSaving) return;
    const payload = trimPayload(form);
    const nextFieldErrors: Partial<Record<keyof TopicFormState, string>> = {};
    if (!payload.title) {
      nextFieldErrors.title = '请填写主题名称';
      setFieldErrors(nextFieldErrors);
      setFormError('');
      return;
    }
    if (!isValidTopicDuration(payload.estimated_duration)) {
      nextFieldErrors.estimated_duration = '时长格式不正确，请使用“2分钟”“1分30秒”或“90秒”';
      setFieldErrors(nextFieldErrors);
      setFormError('');
      return;
    }
    if (!payload.suitable_grade) {
      nextFieldErrors.suitable_grade = '请选择适用年级';
      setFieldErrors(nextFieldErrors);
      setFormError('');
      return;
    }
    if (!payload.content_category) {
      nextFieldErrors.content_category = '请选择主题分类';
      setFieldErrors(nextFieldErrors);
      setFormError('');
      return;
    }
    if (!payload.content_direction) {
      nextFieldErrors.content_direction = '请选择内容方向';
      setFieldErrors(nextFieldErrors);
      setFormError('');
      return;
    }
    if (!isRequiredResourceContent(payload.download_link)) {
      nextFieldErrors.download_link = '请填写领取资源内容';
      setFieldErrors(nextFieldErrors);
      setFormError('');
      return;
    }
    for (const [field, value, label] of [
      ['cover_image', payload.cover_image, '封面地址'],
      ['script_display', payload.script_display, '文稿预览地址'],
      ['video_display', payload.video_display, '视频预览地址'],
      ['ppt_display', payload.ppt_display, 'PPT预览地址'],
    ] as const) {
      if (!isValidPreviewAssetUrl(value)) {
        nextFieldErrors[field] = label + '必须使用 HTTPS 地址或系统上传地址';
        setFieldErrors(nextFieldErrors);
        setFormError('');
        return;
      }
    }

    saveLockRef.current = true;
    setIsSaving(true);
    try {
      if (editingTopic) {
        await updateSpeechTopic(editingTopic.id, payload);
      } else {
        await createSpeechTopic(payload);
      }
      setIsFormOpen(false);
      setFormError('');
      setFieldErrors({});
      await fetchTopics();
    } catch (error) {
      const message = extractApiErrorMessage(error, '保存主题失败');
      setFormError(message);
    } finally {
      setIsSaving(false);
      saveLockRef.current = false;
    }
  };

  const handleToggleActive = async (topic: AdminSpeechTopic) => {
    try {
      if (topic.is_active) {
        await updateSpeechTopic(topic.id, { is_active: false });
        toast.success('主题已下架');
      } else {
        await updateSpeechTopic(topic.id, { is_active: true });
        toast.success('主题已上架');
      }
      await fetchTopics();
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '更新主题状态失败'));
    }
  };

  const handleDelete = async (topic: AdminSpeechTopic) => {
    if (isDeleting) return;
    const confirmed = window.confirm(
      '确定永久删除主题“' +
      topic.title +
      '”吗？\n\n删除主题、资源链接和统计后不可恢复；历史领取记录会保留，COS 文件不会自动删除。'
    );
    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await deleteSpeechTopic(topic.id);
      toast.success('主题已永久删除');
      if (editingTopic?.id === topic.id) {
        setIsFormOpen(false);
        setEditingTopic(null);
      }
      await fetchTopics();
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '永久删除主题失败'));
    } finally {
      setIsDeleting(false);
    }
  };

  const sortedTopics = useMemo(
    () => sortAdminTopicsByMetric(
      filterAdminTopics(topics, { category: categoryFilter, issue: issueFilter }),
      sortMode,
    ),
    [topics, sortMode, categoryFilter, issueFilter],
  );
  // 拖拽排序仅在上架、无搜索、无筛选且按前台顺序查看时可用。
  const canReorder =
    !search.trim() && status === 'active' && sortMode === 'display' && !categoryFilter && issueFilter === 'all';
  const showIssue = (issue: TopicIssueFilter) => {
    setStatus('active');
    setIssueFilter((current) => (current === issue ? 'all' : issue));
  };
  const totalPages = Math.max(1, Math.ceil(sortedTopics.length / PAGE_SIZE));
  const visibleTopics = canReorder
    ? topics
    : sortedTopics.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!canReorder || !over || active.id === over.id) return;
    const oldIndex = topics.findIndex((topic) => topic.id === active.id);
    const newIndex = topics.findIndex((topic) => topic.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const nextTopics = arrayMove(topics, oldIndex, newIndex);
    setTopics(nextTopics);
    try {
      await reorderSpeechTopics(nextTopics.map((topic) => topic.id));
      toast.success('主题排序已保存');
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '保存主题排序失败'));
      await fetchTopics();
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadSpeechTopicImportTemplate();
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '下载模板失败'));
    }
  };

  const handlePreviewImport = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setIsImporting(true);
    try {
      const result = await previewSpeechTopicImport(file);
      setImportPreview(result);
      setIsImportOpen(true);
      if (result.summary.invalid > 0) {
        toast.warning(`导入预览完成，发现 ${result.summary.invalid} 行需要处理`);
      } else {
        toast.success('导入预览通过，可以确认导入');
      }
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '导入预览失败'));
    } finally {
      setIsImporting(false);
    }
  };

  const handleCommitImport = async () => {
    if (!validImportItems.length) {
      toast.error('没有可导入的数据');
      return;
    }
    setIsImporting(true);
    try {
      const result = await commitSpeechTopicImport({ items: validImportItems });
      toast.success(`导入完成：新增 ${result.created} 条`);
      setIsImportOpen(false);
      setImportPreview(null);
      await fetchTopics();
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '确认导入失败'));
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">主题管理</h2>
          <p className="mt-1 text-sm text-muted-foreground">维护前台主题、预览素材、领取资源内容和展示排序。</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleDownloadTemplate}>
            <Download className="mr-1 h-4 w-4" />
            下载模板
          </Button>
          <Button variant="outline" onClick={() => importFileRef.current?.click()} disabled={isImporting}>
            {isImporting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-1 h-4 w-4" />}
            批量导入
          </Button>
          <input
            ref={importFileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={handlePreviewImport}
          />
          <Button onClick={openCreateDialog} className="bg-cta text-cta-foreground hover:bg-cta-hover">
            <Plus className="mr-1 h-4 w-4" />
            新增主题
          </Button>
        </div>
      </div>

      {/* 统计卡：后两张可点击，直接筛出有问题的已上架主题 */}
      {summary && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <TopicStatCard label="已上架" value={summary.active} onClick={() => { setStatus('active'); setIssueFilter('all'); }} active={status === 'active' && issueFilter === 'all'} />
          <TopicStatCard label="已下架" value={summary.inactive} onClick={() => { setStatus('inactive'); setIssueFilter('all'); }} active={status === 'inactive' && issueFilter === 'all'} />
          <TopicStatCard
            label="预览素材缺失"
            hint="封面、视频或 PPT 预览图"
            value={summary.missingPreview}
            warn={summary.missingPreview > 0}
            onClick={() => showIssue('missing-preview')}
            active={issueFilter === 'missing-preview'}
          />
          <TopicStatCard
            label="未配置领取资源"
            hint="家长领取后拿不到内容"
            value={summary.missingResource}
            warn={summary.missingResource > 0}
            onClick={() => showIssue('missing-resource')}
            active={issueFilter === 'missing-resource'}
          />
        </div>
      )}

      <div className="flex flex-col gap-2 rounded-2xl bg-white p-3 shadow-[0_0_0_1px_var(--border)] md:flex-row md:flex-wrap md:items-center">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="搜索主题名称、核心价值观"
            className="pl-9"
          />
        </div>
        <div className="inline-flex rounded-lg bg-muted p-0.5">
          <button
            type="button"
            onClick={() => setStatus('active')}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
              status === 'active' ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            上架
          </button>
          <button
            type="button"
            onClick={() => setStatus('inactive')}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
              status === 'inactive' ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            下架
          </button>
        </div>
        <Select value={categoryFilter || 'all'} onValueChange={(value) => setCategoryFilter(value === 'all' ? '' : value as ContentCategory)}>
          <SelectTrigger className="w-full md:w-[140px]" aria-label="按主题分类筛选">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部分类</SelectItem>
            {categoryOptions.map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sortMode} onValueChange={(value) => setSortMode(value as TopicStatsSortMode)}>
          <SelectTrigger className="w-full md:w-[160px]" aria-label="主题统计排序">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="display">前台展示顺序</SelectItem>
            <SelectItem value="views">浏览次数最多</SelectItem>
            <SelectItem value="redemptions">领取次数最多</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchTopics} disabled={isLoading}>
          {isLoading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />}
          刷新
        </Button>
      </div>

      {issueFilter !== 'all' && (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800">
          <span>正在查看：{issueFilter === 'missing-preview' ? '预览素材缺失' : '未配置领取资源'}的已上架主题</span>
          <Button variant="ghost" size="sm" className="h-7 px-2 text-red-800 hover:bg-red-100" onClick={() => setIssueFilter('all')}>
            查看全部
          </Button>
        </div>
      )}

      {!canReorder && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          搜索、筛选、下架或统计排序状态下暂不支持拖拽；切换为“上架”和“前台展示顺序”并清空搜索和筛选后即可调整完整顺序。
        </p>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={topics.map((topic) => topic.id)} strategy={verticalListSortingStrategy}>
          <div className="overflow-x-auto rounded-2xl bg-white shadow-[0_0_0_1px_var(--border)]">
        <table className="w-full min-w-[1340px] table-auto">
          <thead className="bg-slate-50 text-left text-xs font-medium text-slate-500">
            <tr>
              <th className="w-[80px] whitespace-nowrap px-4 py-3">序号</th>
              <th className="min-w-[300px] whitespace-nowrap px-4 py-3">主题</th>
              <th className="whitespace-nowrap px-4 py-3">状态</th>
              <th className="whitespace-nowrap px-4 py-3" title="每次打开主题详情累计一次，重复打开会重复计数">浏览次数</th>
              <th className="whitespace-nowrap px-4 py-3">领取次数</th>
              <th className="whitespace-nowrap px-4 py-3">时长</th>
              <th className="whitespace-nowrap px-4 py-3">主题分类</th>
              <th className="whitespace-nowrap px-4 py-3">内容方向</th>
              <th className="min-w-[160px] whitespace-nowrap px-4 py-3">素材预览</th>
              <th className="min-w-[240px] whitespace-nowrap px-4 py-3">领取资源</th>
              <th className="sticky right-0 z-10 min-w-[230px] whitespace-nowrap bg-slate-50 px-4 py-3 text-right">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              <tr>
                <td colSpan={11} className="px-4 py-12 text-center text-slate-500">
                  <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
                  加载中
                </td>
              </tr>
            ) : sortedTopics.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-4 py-12 text-center text-slate-500">
                  {topics.length === 0
                    ? `暂无${status === 'active' ? '上架' : '下架'}主题`
                    : '没有符合筛选条件的主题'}
                </td>
              </tr>
            ) : (
              visibleTopics.map((topic, index) => (
                <SortableTopicRow
                  key={topic.id}
                  topic={topic}
                  rank={canReorder ? index + 1 : (page - 1) * PAGE_SIZE + index + 1}
                  canDrag={canReorder}
                  onEdit={openEditDialog}
                  onToggleActive={handleToggleActive}
                />
              ))
            )}
          </tbody>
        </table>
          </div>
          {!canReorder && sortedTopics.length > PAGE_SIZE && (
            <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-4 py-3">
              <span className="text-sm text-slate-500">
                共 {sortedTopics.length} 条{status === 'active' ? '上架' : '下架'}主题，第 {page}/{totalPages} 页
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>
                  上一页
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((current) => current + 1)}
                >
                  下一页
                </Button>
              </div>
            </div>
          )}
        </SortableContext>
      </DndContext>

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTopic ? '编辑主题' : '新增主题'}</DialogTitle>
            <DialogDescription>
              视频、PPT预览图、文稿预览图和封面可以上传本地文件，也可以直接粘贴 COS 或其他可访问链接。单个文件不超过 {MAX_UPLOAD_SIZE_MB}MB，上传中会显示阶段、进度和速度。
            </DialogDescription>
          </DialogHeader>

          {formError && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
              {formError}
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium">主题名称</label>
              <Input
                value={form.title}
                onChange={(event) => updateForm('title', event.target.value)}
                aria-invalid={Boolean(fieldErrors.title)}
                className={fieldErrors.title ? 'border-red-500 focus-visible:ring-red-500' : undefined}
              />
              {fieldErrors.title && <p className="mt-1 text-xs text-red-600">{fieldErrors.title}</p>}
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">适用年级 <span className="text-red-600">*</span></label>
              <Select value={form.suitable_grade} onValueChange={(value: SuitableGrade) => updateForm('suitable_grade', value)}>
                <SelectTrigger aria-invalid={Boolean(fieldErrors.suitable_grade)} className={fieldErrors.suitable_grade ? 'border-red-500' : undefined}>
                  <SelectValue placeholder="请选择年级" />
                </SelectTrigger>
                <SelectContent>
                  {gradeOptions.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
              {fieldErrors.suitable_grade && <p className="mt-1 text-xs text-red-600">{fieldErrors.suitable_grade}</p>}
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">时长</label>
              <Input
                value={form.estimated_duration}
                onChange={(event) => updateForm('estimated_duration', event.target.value)}
                placeholder="例如：2分30秒"
                aria-invalid={Boolean(fieldErrors.estimated_duration)}
                className={fieldErrors.estimated_duration ? 'border-red-500 focus-visible:ring-red-500' : undefined}
              />
              {fieldErrors.estimated_duration && <p className="mt-1 text-xs text-red-600">{fieldErrors.estimated_duration}</p>}
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">主题分类 <span className="text-red-600">*</span></label>
              <Select value={form.content_category} onValueChange={(value: ContentCategory) => updateForm('content_category', value)}>
                <SelectTrigger aria-invalid={Boolean(fieldErrors.content_category)} className={fieldErrors.content_category ? 'border-red-500' : undefined}>
                  <SelectValue placeholder="请选择分类" />
                </SelectTrigger>
                <SelectContent>
                  {categoryOptions.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
              {fieldErrors.content_category && <p className="mt-1 text-xs text-red-600">{fieldErrors.content_category}</p>}
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">内容方向 <span className="text-red-600">*</span></label>
              <Select value={form.content_direction} onValueChange={(value: ContentDirection) => updateForm('content_direction', value)}>
                <SelectTrigger aria-invalid={Boolean(fieldErrors.content_direction)} className={fieldErrors.content_direction ? 'border-red-500' : undefined}>
                  <SelectValue placeholder="请选择方向" />
                </SelectTrigger>
                <SelectContent>
                  {directionOptions.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
              {fieldErrors.content_direction && <p className="mt-1 text-xs text-red-600">{fieldErrors.content_direction}</p>}
            </div>
            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-medium">核心价值观</label>
              <Input value={form.core_value} onChange={(event) => updateForm('core_value', event.target.value)} />
            </div>
            <AssetInput
              label="封面链接"
              value={form.cover_image}
              error={fieldErrors.cover_image}
              success={uploadSuccessField === 'cover_image'}
              accept="image/*"
              isUploading={uploadingField === 'cover_image'}
              uploadProgress={uploadingField === 'cover_image' ? uploadProgress : null}
              onChange={(value) => updateForm('cover_image', value)}
              onUpload={(file) => handleUpload('cover_image', file)}
            />
            <AssetInput
              label="视频预览链接"
              value={form.video_display}
              error={fieldErrors.video_display}
              success={uploadSuccessField === 'video_display'}
              accept="video/*"
              isUploading={uploadingField === 'video_display'}
              uploadProgress={uploadingField === 'video_display' ? uploadProgress : null}
              onChange={(value) => updateForm('video_display', value)}
              onUpload={(file) => handleUpload('video_display', file)}
            />
            <AssetInput
              label="PPT预览图"
              value={form.ppt_display}
              error={fieldErrors.ppt_display}
              success={uploadSuccessField === 'ppt_display'}
              accept="image/*"
              isUploading={uploadingField === 'ppt_display'}
              uploadProgress={uploadingField === 'ppt_display' ? uploadProgress : null}
              onChange={(value) => updateForm('ppt_display', value)}
              onUpload={(file) => handleUpload('ppt_display', file)}
            />
            <AssetInput
              label="文稿预览图"
              value={form.script_display}
              error={fieldErrors.script_display}
              success={uploadSuccessField === 'script_display'}
              accept="image/*"
              isUploading={uploadingField === 'script_display'}
              uploadProgress={uploadingField === 'script_display' ? uploadProgress : null}
              onChange={(value) => updateForm('script_display', value)}
              onUpload={(file) => handleUpload('script_display', file)}
            />
            <div>
              <label className="mb-2 block text-sm font-medium">领取资源内容 <span className="text-red-600">*</span></label>
              <Input
                value={form.download_link}
                onChange={(event) => updateForm('download_link', event.target.value)}
                placeholder="填写领取资源内容（链接、提取码或说明均可）"
                aria-invalid={Boolean(fieldErrors.download_link)}
                className={fieldErrors.download_link ? 'border-red-500 focus-visible:ring-red-500' : undefined}
              />
              {fieldErrors.download_link && <p className="mt-1 text-xs text-red-600">{fieldErrors.download_link}</p>}
            </div>
            <div className="flex items-center justify-between rounded-md border p-3 md:col-span-2">
              <div>
                <div className="text-sm font-medium">上架状态</div>
                <div className="text-xs text-slate-500">关闭后前台不会展示，但后台仍可编辑。</div>
              </div>
              <Switch checked={form.is_active} onCheckedChange={(checked) => updateForm('is_active', checked)} />
            </div>
          </div>

          {editingTopic && (
            <div className="mt-2 flex items-center justify-between gap-4 rounded-md border border-red-200 bg-red-50 px-3 py-3">
              <div>
                <div className="text-sm font-medium text-red-800">危险操作</div>
                <p className="mt-1 text-xs text-red-700">永久删除“{editingTopic.title}”，历史领取记录会保留。</p>
              </div>
              <Button
                type="button"
                variant="outline"
                className="shrink-0 border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800"
                disabled={isDeleting}
                onClick={() => handleDelete(editingTopic)}
              >
                <Trash2 className="mr-1 h-4 w-4" />
                永久删除
              </Button>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFormOpen(false)} disabled={isSaving || Boolean(uploadingField)}>取消</Button>
            <Button onClick={handleSave} disabled={isSaving || Boolean(uploadingField)}>
              {isSaving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>批量导入预览</DialogTitle>
            <DialogDescription>
              先检查数据再写入数据库。错误行不会导入，需要改表格后重新上传。
            </DialogDescription>
          </DialogHeader>

          {importPreview ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                <SummaryBox label="总行数" value={importPreview.summary.total} />
                <SummaryBox label="新增" value={importPreview.summary.creatable} />
                <SummaryBox label="错误/冲突" value={importPreview.summary.invalid} tone={importPreview.summary.invalid > 0 ? 'danger' : 'normal'} />
              </div>
              <div className="max-h-[420px] overflow-auto rounded-lg border">
                <table className="w-full min-w-[760px] table-auto text-sm">
                  <thead className="bg-slate-50 text-left text-xs text-slate-500">
                    <tr>
                      <th className="px-3 py-2">行号</th>
                      <th className="px-3 py-2">主题</th>
                      <th className="px-3 py-2">动作</th>
                      <th className="px-3 py-2">结果</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {importPreview.rows.map((row) => (
                      <tr key={row.row_number}>
                        <td className="px-3 py-2">{row.row_number}</td>
                        <td className="px-3 py-2">{row.topic_name || '-'}</td>
                        <td className="px-3 py-2">
                          <Badge variant={row.action === 'error' ? 'destructive' : 'outline'}>
                            {row.action === 'create' ? '新增' : '错误/冲突'}
                          </Badge>
                        </td>
                        <td className="px-3 py-2">
                          {row.errors.length > 0 ? (
                            <div className="flex items-start gap-2 text-red-600">
                              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                              <span>{row.errors.join('；')}</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-emerald-600">
                              <CheckCircle2 className="h-4 w-4" />
                              可导入
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsImportOpen(false)}>取消</Button>
            <Button onClick={handleCommitImport} disabled={isImporting || validImportItems.length === 0}>
              {isImporting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              确认导入 {validImportItems.length} 条
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SortableTopicRow(props: {
  topic: AdminSpeechTopic;
  rank: number;
  canDrag: boolean;
  onEdit: (topic: AdminSpeechTopic) => void;
  onToggleActive: (topic: AdminSpeechTopic) => void;
}) {
  const { topic, rank, canDrag, onEdit, onToggleActive } = props;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: topic.id, disabled: !canDrag });
  const style = {
    transform: transform
      ? 'translate3d(' + transform.x + 'px, ' + transform.y + 'px, 0)'
      : undefined,
    transition,
    position: isDragging ? 'relative' as const : undefined,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <tr ref={setNodeRef} style={style} className="bg-white hover:bg-slate-50">
      <td className="px-4 py-3 text-sm font-semibold text-orange-600">
        <div className="flex items-center gap-1">
          <button
            type="button"
            className={canDrag ? 'cursor-grab rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 active:cursor-grabbing' : 'cursor-not-allowed rounded p-1 text-slate-300'}
            aria-label={canDrag ? '拖拽调整排序' : '筛选状态下不可排序'}
            disabled={!canDrag}
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
          {rank}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="h-12 w-20 overflow-hidden rounded border bg-slate-100">
            {topic.cover_image ? (
              <img src={topic.cover_image} alt={topic.title} className="h-full w-full object-cover" />
            ) : null}
          </div>
          <div>
            <div className="font-medium text-slate-900">{topic.title}</div>
            <div className="max-w-[240px] truncate text-xs text-slate-500">{topic.core_value || '-'}</div>
          </div>
        </div>
      </td>
      <td className="whitespace-nowrap px-4 py-3">
        <Badge variant={topic.is_active ? 'default' : 'secondary'}>
          {topic.is_active ? '上架' : '下架'}
        </Badge>
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-slate-700" title="主题详情累计打开次数">
        {topic.view_count}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-slate-700">
        {topic.redeem_count}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-700">{topic.estimated_duration || '-'}</td>
      <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-700">{CONTENT_CATEGORY_LABELS[topic.content_category]}</td>
      <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-700">{CONTENT_DIRECTION_LABELS[topic.content_direction]}</td>
      <td className="px-4 py-3 text-sm text-slate-600">
        <div className="flex flex-wrap gap-1">
          {topic.cover_image ? <Badge variant="outline">封面</Badge> : null}
          {topic.video_display ? <Badge variant="outline">视频</Badge> : null}
          {topic.ppt_display ? <Badge variant="outline">PPT预览图</Badge> : null}
          {topic.script_display ? <Badge variant="outline">文稿预览图</Badge> : null}
          {getMissingPreviewLabels(topic).map((label) => (
            <Badge key={label} variant="secondary" className="bg-red-50 text-red-700">缺{label}</Badge>
          ))}
        </div>
      </td>
      <td className="max-w-[240px] px-4 py-3 text-sm text-slate-600">
        {isMissingResource(topic) ? (
          <Badge variant="secondary" className="bg-red-50 text-red-700">未配置</Badge>
        ) : (
          <span className="block truncate" title={topic.download_link || '-'}>
            {topic.download_link}
          </span>
        )}
      </td>
      <td className="sticky right-0 z-[1] min-w-[230px] bg-white px-4 py-3">
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => onEdit(topic)}>
            <Edit className="mr-1 h-4 w-4" />
            编辑
          </Button>
          <Button variant="outline" size="sm" onClick={() => onToggleActive(topic)}>
            {topic.is_active ? <EyeOff className="mr-1 h-4 w-4" /> : <Eye className="mr-1 h-4 w-4" />}
            {topic.is_active ? '下架' : '上架'}
          </Button>
        </div>
      </td>
    </tr>
  );
}

function AssetInput(props: {
  label: string;
  value: string;
  accept: string;
  error?: string;
  success?: boolean;
  isUploading: boolean;
  uploadProgress?: UploadProgress | null;
  onChange: (value: string) => void;
  onUpload: (file?: File) => void;
}) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium">{props.label}</label>
      <div className="flex gap-2">
        <Input
          value={props.value}
          onChange={(event) => props.onChange(event.target.value)}
          placeholder="可粘贴 HTTPS 地址"
          aria-invalid={Boolean(props.error)}
          className={props.error ? 'border-red-500 focus-visible:ring-red-500' : undefined}
        />
        <Button asChild variant="outline" className="shrink-0">
          <label>
            {props.isUploading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />}
            上传
            <input
              type="file"
              accept={props.accept}
              className="hidden"
              disabled={props.isUploading}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.currentTarget.value = '';
                props.onUpload(file);
              }}
            />
          </label>
        </Button>
      </div>
      <p className="mt-1 text-xs text-slate-500">单个文件不超过 {MAX_UPLOAD_SIZE_MB}MB</p>
      {props.uploadProgress && <UploadProgressView progress={props.uploadProgress} />}
      {props.error && <p className="mt-1 text-xs text-red-600">{props.error}</p>}
      {props.success && <p className="mt-1 text-xs text-emerald-700">上传成功，已写入表单</p>}
      {props.value && (
        <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50/60 p-2">
          <p className="text-xs font-medium text-emerald-700">已设置，可在保存后继续预览</p>
          {props.accept.startsWith('image/') ? (
            <img src={props.value} alt={props.label} className="mt-2 h-20 max-w-full rounded object-contain object-left" />
          ) : (
            <video src={props.value} controls preload="metadata" className="mt-2 h-20 max-w-full rounded" />
          )}
        </div>
      )}
    </div>
  );
}

function formatElapsed(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  if (seconds < 60) return `${seconds}秒`;
  return `${Math.floor(seconds / 60)}分${seconds % 60}秒`;
}

function formatSpeed(bytesPerSecond: number | null): string {
  return bytesPerSecond && bytesPerSecond > 0
    ? `${formatUploadSize(bytesPerSecond)}/秒`
    : '计算中';
}

function formatRemaining(progress: UploadProgress): string | null {
  if (progress.percent === null || !progress.bytesPerSecond || progress.bytesPerSecond <= 0) return null;
  const remainingSeconds = Math.max(0, Math.ceil((progress.total - progress.loaded) / progress.bytesPerSecond));
  return `预计剩 ${remainingSeconds < 60 ? `${remainingSeconds}秒` : `${Math.floor(remainingSeconds / 60)}分${remainingSeconds % 60}秒`}`;
}

function UploadProgressView({ progress }: { progress: UploadProgress }) {
  const phaseLabel = {
    preparing: '准备上传',
    uploading: '正在上传到应用服务器',
    storing: '正在写入 COS，等待服务器确认',
  }[progress.phase];

  return (
    <div className="mt-2 rounded-md border border-orange-200 bg-orange-50/60 p-2" role="status" aria-live="polite">
      <div className="flex items-center justify-between gap-2 text-xs font-medium text-orange-800">
        <span>{phaseLabel}</span>
        <span>{progress.percent === null ? '处理中' : `${progress.percent}%`}</span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-orange-100">
        <div
          className={progress.percent === null ? 'h-full w-1/3 animate-pulse rounded-full bg-orange-500' : 'h-full rounded-full bg-orange-500 transition-[width]'}
          style={progress.percent === null ? undefined : { width: `${progress.percent}%` }}
        />
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-orange-700">
        <span>{formatUploadSize(progress.loaded)} / {formatUploadSize(progress.total)}</span>
        <span>{formatSpeed(progress.bytesPerSecond)}</span>
        <span>已用 {formatElapsed(progress.elapsedMs)}</span>
        {formatRemaining(progress) && <span>{formatRemaining(progress)}</span>}
      </div>
    </div>
  );
}

function SummaryBox(props: { label: string; value: number; tone?: 'normal' | 'danger' }) {
  return (
    <div className={`rounded-md border p-3 ${props.tone === 'danger' ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
      <div className="text-xs text-slate-500">{props.label}</div>
      <div className="mt-1 text-xl font-semibold text-slate-900">{props.value}</div>
    </div>
  );
}

function TopicStatCard({
  label,
  value,
  hint,
  warn = false,
  active = false,
  onClick,
}: {
  label: string;
  value: number;
  hint?: string;
  warn?: boolean;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-2xl bg-white px-4 py-3.5 text-left transition-shadow hover:shadow-md ${
        active
          ? 'shadow-[0_0_0_2px_var(--cta)]'
          : warn
            ? 'shadow-[0_0_0_1px_#F2CFC6]'
            : 'shadow-[0_0_0_1px_var(--border)]'
      }`}
    >
      <div className="text-[13px] text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${warn ? 'text-red-700' : 'text-foreground'}`}>{value}</div>
      {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
    </button>
  );
}
